import {
  calculateCaptureBudget,
  CaptureStore
} from "./capture-store.js";
import { CaptureUploader } from "./capture-uploader.js";

const MIME_CANDIDATES = Object.freeze([
  "video/mp4;codecs=avc1.42E01E,mp4a.40.2",
  "video/mp4",
  "video/webm;codecs=vp8,opus",
  "video/webm"
]);

function controllerError(code, message) {
  const error = new Error(message);
  error.code = code;
  return error;
}

function randomUuid(cryptoImpl) {
  if (typeof cryptoImpl.randomUUID === "function") {
    return cryptoImpl.randomUUID();
  }
  const bytes = cryptoImpl.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map((value) => value.toString(16).padStart(2, "0"));
  return (
    `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-` +
    `${hex.slice(6, 8).join("")}-${hex.slice(8, 10).join("")}-` +
    hex.slice(10).join("")
  );
}

export function chooseCaptureMimeType(MediaRecorderCtor) {
  if (
    typeof MediaRecorderCtor !== "function" ||
    typeof MediaRecorderCtor.isTypeSupported !== "function"
  ) {
    return "";
  }
  return MIME_CANDIDATES.find((mime) =>
    MediaRecorderCtor.isTypeSupported(mime)
  ) || "";
}

export function probeCaptureSupport({
  secureContext = globalThis.isSecureContext,
  MediaRecorderCtor = globalThis.MediaRecorder,
  MediaStreamCtor = globalThis.MediaStream,
  indexedDBImpl = globalThis.indexedDB,
  cryptoImpl = globalThis.crypto
} = {}) {
  if (!secureContext) return { supported: false, reason: "secure_context" };
  if (typeof MediaStreamCtor !== "function") {
    return { supported: false, reason: "media_stream" };
  }
  if (!indexedDBImpl?.open) return { supported: false, reason: "indexeddb" };
  if (!cryptoImpl?.subtle?.digest || !cryptoImpl?.getRandomValues) {
    return { supported: false, reason: "webcrypto" };
  }
  if (typeof Blob?.prototype?.arrayBuffer !== "function") {
    return { supported: false, reason: "blob_array_buffer" };
  }
  const mimeType = chooseCaptureMimeType(MediaRecorderCtor);
  if (!mimeType) return { supported: false, reason: "media_recorder" };
  return { supported: true, reason: null, mimeType };
}

export async function sha256Blob(blob, cryptoImpl = globalThis.crypto) {
  const buffer = await blob.arrayBuffer();
  const digest = await cryptoImpl.subtle.digest("SHA-256", buffer);
  return [...new Uint8Array(digest)]
    .map((value) => value.toString(16).padStart(2, "0"))
    .join("");
}

export class CaptureController {
  constructor({
    store = new CaptureStore(),
    uploader = null,
    fetchImpl = globalThis.fetch?.bind(globalThis),
    MediaRecorderCtor = globalThis.MediaRecorder,
    MediaStreamCtor = globalThis.MediaStream,
    cryptoImpl = globalThis.crypto,
    storageManager = globalThis.navigator?.storage,
    secureContext = globalThis.isSecureContext,
    performanceImpl = globalThis.performance,
    now = () => Date.now(),
    online = () => globalThis.navigator?.onLine !== false,
    onState = () => {}
  } = {}) {
    this.store = store;
    this.MediaRecorderCtor = MediaRecorderCtor;
    this.MediaStreamCtor = MediaStreamCtor;
    this.cryptoImpl = cryptoImpl;
    this.storageManager = storageManager;
    this.secureContext = secureContext;
    this.performanceImpl = performanceImpl;
    this.now = now;
    this.online = online;
    this.onState = onState;
    this.uploader = uploader || new CaptureUploader({
      store,
      fetchImpl,
      now,
      online,
      onState: (state, error) => this.handleUploaderState(state, error)
    });
    this.state = "idle";
    this.authorization = null;
    this.recorder = null;
    this.captureTracks = [];
    this.captureStream = null;
    this.epoch = null;
    this.sequence = 0;
    this.lastEndMs = 0;
    this.chunkChain = Promise.resolve();
    this.acceptChunks = false;
    this.hiddenStartedMs = null;
    this.stopping = false;
    this.startAttempt = 0;
    this.rotationPromise = null;
    this.completedEpochs = [];
    this.completedSyncPromise = null;
    this.finalizationPending = false;
    this.finalizePromise = null;
    this.recoveryEpochIds = new Set();
    this.localBudgetBytes = 0;
  }

  setState(state, detail = null) {
    this.state = state;
    this.onState({ state, detail });
  }

  handleUploaderState(state, error) {
    if (state === "blocked") {
      void this.block(error);
      return;
    }
    if (
      !this.stopping &&
      !["blocked", "degraded", "stopped"].includes(this.state) &&
      ["uploading", "pending_upload", "synced"].includes(state)
    ) {
      this.setState(state, error || null);
    }
  }

  assertStartActive(attempt) {
    if (
      this.stopping ||
      attempt !== this.startAttempt ||
      ["blocked", "stopped"].includes(this.state)
    ) {
      throw controllerError("CAPTURE_START_ABORTED", "Capture start was cancelled.");
    }
  }

  async estimateBudget(configuredMaxBytes) {
    let estimate = {};
    try {
      await this.storageManager?.persist?.();
      estimate = await this.storageManager?.estimate?.() || {};
    } catch {
      // Quota estimates and persistence are advisory. IDB writes remain guarded.
    }
    return calculateCaptureBudget({
      configuredMaxBytes,
      quota: Number(estimate.quota),
      usage: Number(estimate.usage)
    });
  }

  async start({ audioTrack, videoTrack }) {
    if (this.state !== "idle") return this.getSnapshot();
    const attempt = ++this.startAttempt;
    this.stopping = false;
    this.setState("probing");
    const support = probeCaptureSupport({
      secureContext: this.secureContext,
      MediaRecorderCtor: this.MediaRecorderCtor,
      MediaStreamCtor: this.MediaStreamCtor,
      indexedDBImpl: this.store.indexedDBImpl,
      cryptoImpl: this.cryptoImpl
    });
    if (!support.supported) {
      this.setState("unsupported", support.reason);
      return this.getSnapshot();
    }
    const sourceTracks = [audioTrack, videoTrack].map((track) =>
      track?.getMediaStreamTrack?.()
    );
    if (
      sourceTracks.some(
        (track) => !track || track.readyState !== "live" || typeof track.clone !== "function"
      )
    ) {
      this.setState("unsupported", "local_tracks");
      return this.getSnapshot();
    }

    try {
      await this.store.open();
      this.assertStartActive(attempt);
      await this.store.prune(this.now());
      this.assertStartActive(attempt);
      const authorization = await this.uploader.authorize();
      this.assertStartActive(attempt);
      this.authorization = authorization;
      if (
        ["complete", "complete_with_gaps", "incomplete"].includes(
          authorization.captureState
        )
      ) {
        this.setState("synced", authorization.captureState);
        return this.getSnapshot();
      }
      this.localBudgetBytes = await this.estimateBudget(
        this.authorization.maxPendingBytes
      );
      this.assertStartActive(attempt);
      if (this.localBudgetBytes < this.authorization.maxChunkBytes) {
        throw controllerError(
          "CAPTURE_LOCAL_LIMIT",
          "Insufficient local storage budget."
        );
      }
      const priorEpochs = await this.store.listEpochs(
        this.authorization.captureId
      );
      this.recoveryEpochIds = new Set(
        priorEpochs
          .filter((epoch) => epoch.state !== "sealed")
          .map((epoch) => epoch.epochId)
      );
      if (this.recoveryEpochIds.size) {
        try {
          await this.recoverPriorEpochs();
        } catch (error) {
          if (error?.code !== "CAPTURE_NETWORK" && this.online()) throw error;
          this.setState("pending_upload", error?.code || null);
        }
      }
      this.assertStartActive(attempt);
      this.captureTracks = sourceTracks.map((track) => track.clone());
      this.captureStream = new this.MediaStreamCtor(this.captureTracks);
      await this.startEpoch(support.mimeType, attempt);
      return this.getSnapshot();
    } catch (error) {
      if (error?.code === "CAPTURE_START_ABORTED") {
        this.acceptChunks = false;
        this.stopCaptureTracks();
        return this.getSnapshot();
      }
      this.degrade(error);
      return this.getSnapshot();
    }
  }

  async startEpoch(mimeType, attempt = this.startAttempt) {
    this.assertStartActive(attempt);
    const epochId = randomUuid(this.cryptoImpl);
    const startedWallMs = this.now();
    this.recorder = new this.MediaRecorderCtor(this.captureStream, { mimeType });
    const actualMime = String(this.recorder.mimeType || mimeType);
    this.epoch = {
      captureId: this.authorization.captureId,
      epochId,
      mimeType: actualMime,
      startedWallMs,
      startedMonotonicMs: this.performanceImpl.now(),
      state: "active",
      lastSequence: -1,
      lastEndMs: 0,
      gaps: []
    };
    this.sequence = 0;
    this.lastEndMs = 0;
    await this.store.putEpoch(this.epoch);
    this.assertStartActive(attempt);
    this.acceptChunks = true;
    this.recorder.addEventListener("dataavailable", (event) => {
      this.chunkChain = this.chunkChain
        .then(() => this.handleChunk(event.data))
        .catch((error) => this.degrade(error));
    });
    this.recorder.addEventListener("error", () => {
      this.noteGap("recorder_error");
      this.degrade(
        controllerError("CAPTURE_RECORDER_ERROR", "MediaRecorder failed.")
      );
    });
    this.recorder.start(this.authorization.targetTimesliceMs || 4000);
    this.setState("recording");
  }

  epochElapsedMs() {
    if (!this.epoch) return 0;
    return Math.max(
      this.lastEndMs,
      Math.round(this.performanceImpl.now() - this.epoch.startedMonotonicMs)
    );
  }

  sealedEpochSnapshot(epoch = this.epoch) {
    if (!epoch) return null;
    const durationMs = Math.max(this.lastEndMs, this.epochElapsedMs());
    return {
      ...epoch,
      lastSequence: this.sequence - 1,
      lastEndMs: durationMs,
      endedWallMs: epoch.startedWallMs + durationMs,
      durationMs,
      gaps: epoch.gaps || []
    };
  }

  handleSyncError(error) {
    if (error?.status === 423) {
      void this.block(error);
    } else if (
      this.stopping ||
      error?.code === "CAPTURE_NETWORK" ||
      !this.online()
    ) {
      this.setState("pending_upload", error?.code || null);
    } else {
      this.degrade(error);
    }
  }

  queueCompletedEpoch(epoch) {
    if (!epoch) return;
    const index = this.completedEpochs.findIndex(
      (item) => item.epochId === epoch.epochId
    );
    if (index >= 0) this.completedEpochs[index] = epoch;
    else this.completedEpochs.push(epoch);
  }

  async syncCompletedEpochs() {
    if (this.completedSyncPromise) return this.completedSyncPromise;
    this.completedSyncPromise = (async () => {
      while (this.completedEpochs.length) {
        const epoch = this.completedEpochs[0];
        await this.uploader.drain();
        await this.uploader.sealEpoch(epoch);
        await this.store.sealEpoch(epoch.captureId, epoch.epochId);
        this.completedEpochs.shift();
      }
    })().finally(() => {
      this.completedSyncPromise = null;
    });
    return this.completedSyncPromise;
  }

  async recoverPriorEpochs() {
    if (!this.recoveryEpochIds.size) return;
    await this.uploader.recoverUnsealedEpochs({
      onlyEpochIds: this.recoveryEpochIds
    });
    this.recoveryEpochIds.clear();
  }

  async rotateInactiveEpoch() {
    if (
      this.rotationPromise ||
      this.stopping ||
      !this.epoch ||
      this.recorder?.state !== "inactive"
    ) return this.rotationPromise;
    const attempt = this.startAttempt;
    this.rotationPromise = (async () => {
      this.acceptChunks = false;
      await this.chunkChain.catch(() => undefined);
      this.assertStartActive(attempt);
      const previous = this.sealedEpochSnapshot();
      if (!previous) return;
      await this.store.putEpoch({ ...previous, state: "active" });
      this.queueCompletedEpoch(previous);
      await this.startEpoch(previous.mimeType, attempt);
      void this.syncCompletedEpochs().catch((error) => this.handleSyncError(error));
    })().catch((error) => this.handleSyncError(error)).finally(() => {
      this.rotationPromise = null;
    });
    return this.rotationPromise;
  }

  noteGap(reason, startMs = this.lastEndMs, endMs = this.epochElapsedMs()) {
    if (!this.epoch) return;
    const gap = {
      reason,
      startMs: Math.max(0, Math.round(startMs)),
      endMs: Math.max(Math.round(startMs), Math.round(endMs))
    };
    this.epoch.gaps = [...(this.epoch.gaps || []), gap].slice(-32);
    void this.store.addGap(
      this.epoch.captureId,
      this.epoch.epochId,
      gap
    ).catch(() => undefined);
  }

  async handleChunk(blob) {
    if (!this.acceptChunks || !this.epoch) return;
    const endMs = Math.max(this.lastEndMs + 1, this.epochElapsedMs());
    const startMs = this.lastEndMs;
    this.lastEndMs = endMs;
    if (!blob || blob.size < 1) {
      this.noteGap("empty_chunk", startMs, endMs);
      return;
    }
    if (blob.size > this.authorization.maxChunkBytes) {
      this.noteGap("recorder_error", startMs, endMs);
      throw controllerError(
        "CAPTURE_CHUNK_TOO_LARGE",
        "Browser emitted an oversized capture chunk."
      );
    }
    const mimeType = String(blob.type || this.epoch.mimeType);
    const sha256 = await sha256Blob(blob, this.cryptoImpl);
    const chunk = {
      captureId: this.epoch.captureId,
      epochId: this.epoch.epochId,
      sequence: this.sequence,
      startMs,
      endMs,
      mimeType,
      byteSize: blob.size,
      sha256,
      blob,
      createdAt: this.now()
    };
    try {
      await this.store.putPendingChunk(chunk, this.localBudgetBytes);
    } catch (error) {
      this.noteGap("local_storage", startMs, endMs);
      throw error;
    }
    this.epoch.lastSequence = this.sequence;
    this.epoch.lastEndMs = endMs;
    this.sequence += 1;
    void this.uploader.drain().catch((error) => {
      if (error.status === 423) void this.block(error);
      else if (error.code !== "CAPTURE_NETWORK") this.degrade(error);
    });
  }

  requestData() {
    if (this.recorder?.state === "recording") {
      try {
        this.recorder.requestData();
      } catch {
        // A concurrent stop can make requestData invalid; stop still flushes.
      }
    }
  }

  handleVisibilityChange(hidden) {
    if (!this.epoch || this.stopping) return;
    if (hidden) {
      this.hiddenStartedMs = this.epochElapsedMs();
      this.requestData();
      return;
    }
    if (this.hiddenStartedMs !== null) {
      this.noteGap("page_hidden", this.hiddenStartedMs, this.epochElapsedMs());
      this.hiddenStartedMs = null;
    }
    if (this.recorder?.state === "inactive" && this.state !== "degraded") {
      void this.rotateInactiveEpoch();
    }
    void this.uploader.drain().catch(() => undefined);
  }

  setTrackEnabled(kind, enabled) {
    const targetKind = kind === "audio" ? "audio" : "video";
    for (const track of this.captureTracks) {
      if (track.kind === targetKind) track.enabled = Boolean(enabled);
    }
  }

  handleTrackEnded() {
    this.noteGap("track_ended");
    this.degrade(controllerError("CAPTURE_TRACK_ENDED", "Capture track ended."));
  }

  stopCaptureTracks() {
    for (const track of this.captureTracks) {
      try {
        track.stop();
      } catch {
        // Clone cleanup is best effort and never touches the Agora source track.
      }
    }
    this.captureTracks = [];
    this.captureStream = null;
  }

  degrade(error) {
    if (
      this.stopping ||
      ["degraded", "blocked", "stopped"].includes(this.state)
    ) return;
    this.acceptChunks = false;
    try {
      if (this.recorder?.state !== "inactive") this.recorder.stop();
    } catch {
      // Recorder cleanup is best effort after local failure.
    }
    this.stopCaptureTracks();
    this.setState("degraded", error?.code || "CAPTURE_LOCAL_FAILED");
  }

  async finishFinalization() {
    if (this.finalizePromise) return this.finalizePromise;
    this.finalizePromise = (async () => {
      await this.recoverPriorEpochs();
      await this.syncCompletedEpochs();
      const finalized = await this.uploader.finalizeIfDrained();
      this.finalizationPending = finalized.pending;
      this.setState(finalized.pending ? "pending_upload" : "synced");
      return finalized;
    })().catch(async (error) => {
      this.finalizationPending = true;
      if (error?.status === 423 && this.authorization?.captureId) {
        await this.store.deletePending(this.authorization.captureId)
          .catch(() => undefined);
        this.setState("blocked", error.code);
      } else {
        this.setState("pending_upload", error?.code || null);
      }
      return { pending: true, manifest: null };
    }).finally(() => {
      this.finalizePromise = null;
    });
    return this.finalizePromise;
  }

  async stop({ purge = false } = {}) {
    if (["stopped", "blocked", "unsupported"].includes(this.state)) return;
    this.stopping = true;
    this.startAttempt += 1;
    if (this.hiddenStartedMs !== null) {
      this.noteGap("page_hidden", this.hiddenStartedMs, this.epochElapsedMs());
      this.hiddenStartedMs = null;
    }
    if (this.recorder && this.recorder.state !== "inactive") {
      const stopped = new Promise((resolve) => {
        this.recorder.addEventListener("stop", resolve, { once: true });
      });
      try {
        this.recorder.stop();
        let timeoutId;
        const timeout = new Promise((resolve) => {
          timeoutId = setTimeout(resolve, 1500);
        });
        await Promise.race([stopped, timeout]);
        clearTimeout(timeoutId);
      } catch {
        // Existing chunks remain resumable even if the final flush fails.
      }
    }
    await this.chunkChain.catch(() => undefined);
    this.acceptChunks = false;
    this.stopCaptureTracks();
    if (purge && this.authorization?.captureId) {
      await this.store.deletePending(this.authorization.captureId).catch(() => undefined);
      await this.store.prune(this.now()).catch(() => undefined);
      this.setState("blocked");
      return;
    }
    if (this.epoch && this.authorization) {
      const sealed = this.sealedEpochSnapshot();
      await this.store.putEpoch({ ...sealed, state: "active" }).catch(() => undefined);
      this.queueCompletedEpoch(sealed);
      this.finalizationPending = true;
      await this.finishFinalization();
    }
    if (!["synced", "pending_upload", "blocked"].includes(this.state)) {
      this.setState("stopped");
    }
    await this.store.prune(this.now()).catch(() => undefined);
  }

  async block(error = null) {
    if (this.state === "blocked") return;
    this.stopping = true;
    this.startAttempt += 1;
    this.acceptChunks = false;
    try {
      if (this.recorder?.state !== "inactive") this.recorder.stop();
    } catch {
      // Privacy cleanup continues even if the recorder already stopped.
    }
    this.stopCaptureTracks();
    if (this.authorization?.captureId) {
      await this.store.deletePending(this.authorization.captureId).catch(() => undefined);
    }
    this.setState("blocked", error?.code || null);
  }

  handleOnline() {
    if (!this.authorization || ["blocked", "degraded"].includes(this.state)) return;
    if (this.finalizationPending) {
      void this.finishFinalization();
      return;
    }
    void this.recoverPriorEpochs()
      .then(() => this.syncCompletedEpochs())
      .then(() => this.uploader.drain())
      .catch((error) => this.handleSyncError(error));
  }

  getSnapshot() {
    return {
      state: this.state,
      captureId: this.authorization?.captureId || null,
      epochId: this.epoch?.epochId || null,
      sequence: this.sequence,
      lastEndMs: this.lastEndMs,
      localBudgetBytes: this.localBudgetBytes,
      pendingOnly: this.state === "pending_upload"
    };
  }
}
